import { __awaiter } from "tslib";
import "./login.component.scss";
import { IntegratedDialogError } from "@tedivo/tedivo-ui";
import { TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import { createScreen } from "../../../helpers/createScreen";
import awsCognito from "../../../../app/security/awsCognito";
import { getTranslation, i18nReactive } from "../../../../app/i18/i18tn";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import { MultiStepLogin } from "./multiStepLogin";
import Services from "../../../../app/services";
import { getPreferencesValue } from "@tedivo/tedivo-preferences";
import globalStore from "../../../../app/stores/globalStore";
import goSquared from "../../../../app/tracking/goSquared";
import { passwordCompliesWithCriteria } from "@tedivo/tvd-api-models";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../../app/router/routes";
import router from "../../../../app/router";
import securityModule from "../../../../app/security/SecurityModule";
import { setAppTitle } from "../../../../app/setAppTitle";
import { showDetailedCriteriaCompliance } from "../../my-organization/change-password.component";
import topMessageElement from "../../../layout/top-tools/getTopMessageElement";
import { z } from "zod";
export class TVDAuthLoginComponent extends HTMLElement {
    constructor() {
        super();
        this.didChangePassword = false;
        this.prevLoginStatus = undefined;
        this.onDisconnectedCalls = [];
        this.dialogError = new IntegratedDialogError(this, false, getTranslation("general:common.close"));
        this.returnUrl = routeFns.myCloud();
        this.doesAcceptMarketing = undefined;
        /** Replaces the UI */
        this.setCurrentFormAndNode = (fan) => {
            this.currentFormAndNode = fan;
            if ((fan === null || fan === void 0 ? void 0 : fan.formId) === "login") {
                createLogoH1Replacement(this.h1Node);
                this.prepend(this.h1Node);
            }
            if (fan === null || fan === void 0 ? void 0 : fan.node) {
                this.divWithSpinner.setLoading(false);
                removeChildren(this.divWithSpinner);
                this.divWithSpinner.appendChild(fan.node);
            }
        };
        this.onShowScreen = () => {
            this.showScreen(false);
        };
        /** Shows/Replaces the log-in screen based on securityModule.isLoggedIn, force = false */
        this.showScreen = (force = false) => {
            var _a;
            const { isLoggedIn } = securityModule;
            if (isLoggedIn) {
                router.navigate(routeFns.myProfile(), globalStore.hasShownOrgSelector ? undefined : { showOrgSelector: true }, true);
                return;
            }
            if (this.prevLoginStatus === isLoggedIn && !force)
                return;
            setAppTitle(getTranslation("auth:logIn"));
            goSquared.addEvent("Log-In - Show page");
            const state = router.currentState;
            const queryString = router.getRouteQuerystring();
            const multiStepLogin = new MultiStepLogin(this.i18nCR, state, queryString, this.onSubmitLoginForm);
            const formAndNodeLogin = createLoginPart({
                multiStepLogin,
                i18nCR: this.i18nCR,
                initialCreateAccount: (_a = window.location.hash) === null || _a === void 0 ? void 0 : _a.includes("newAccount"),
            });
            this.setCurrentFormAndNode(Object.assign(Object.assign({}, formAndNodeLogin), { formId: "login" }));
            if (queryString.returnUrl)
                this.returnUrl = queryString.returnUrl;
            this.prevLoginStatus = isLoggedIn;
        };
        this.onSubmitLoginForm = (values) => __awaiter(this, void 0, void 0, function* () {
            this.divWithSpinner.setLoading(true);
            const initialAuthResp = yield securityModule.authenticate(values.username, values.password);
            try {
                goSquared.addEvent("Log-In - Submit credentials");
                yield this.onSubmitAuthForm(initialAuthResp, values.username);
            }
            catch (e) {
                console.log(e);
                this.showError({
                    errorCode: e,
                    message: "",
                    translationKey: "errors:loginAttemptIncorrect",
                });
            }
            this.divWithSpinner.setLoading(false);
        });
        this.onSubmitAuthForm = (authResp, username) => __awaiter(this, void 0, void 0, function* () {
            // console.log("authResp", authResp);
            switch (authResp.code) {
                case "ok":
                    {
                        if (this.didChangePassword)
                            Services.accounts.notifyNewPassword();
                        if (this.doesAcceptMarketing !== undefined) {
                            Services.accounts.updateUserMarketingPreferences(this.doesAcceptMarketing);
                            this.doesAcceptMarketing = undefined;
                        }
                        topMessageElement.planIsExpired = securityModule.planIsExpired;
                        const shouldShowMfa = securityModule.organizationMfaForAllUsers &&
                            !securityModule.isMfaEnabled;
                        if (shouldShowMfa) {
                            router.navigate(routeFns.myProfile(true), undefined, true);
                        }
                        else {
                            const hasMultipleOrgs = awsCognito.allOrganizationsInfo.length > 1;
                            const effectiveOrgs = awsCognito.allEffectiveOrganizationsInfo;
                            const hasNoEnabledOrgs = effectiveOrgs.length === 0;
                            if (hasNoEnabledOrgs) {
                                router.navigate(routeFns.myProfile(), undefined, true);
                            }
                            else if (hasMultipleOrgs) {
                                const preferredOrgId = getPreferencesValue("preferredOrgId");
                                if (preferredOrgId) {
                                    awsCognito.changeCurrentOrganization(preferredOrgId);
                                    if (awsCognito.currentOrganizationId) {
                                        router.navigate(routeFns.myCloud(), undefined, true);
                                    }
                                    else {
                                        router.navigate(routeFns.myProfile(shouldShowMfa), { showOrgSelector: true }, true);
                                    }
                                }
                                else {
                                    router.navigate(routeFns.myProfile(shouldShowMfa), { showOrgSelector: true }, true);
                                }
                            }
                            else {
                                router.navigate(this.returnUrl || routeFns.myCloud(), undefined, true);
                            }
                        }
                    }
                    break;
                case "failure":
                    {
                        if (authResp.error.name === "UserNotFoundException") {
                            this.showError("auth:loggedInFailed");
                            break;
                        }
                        const errorMessage = authResp.error.message || "";
                        let translationKey = errorMessage;
                        if (errorMessage === "ORG_DISABLED" ||
                            errorMessage === "USER_DISABLED" ||
                            errorMessage === "EXPIRED_SUBSCRIPTION") {
                            translationKey = `enums:EnumEnabledStatus.${errorMessage}`;
                        }
                        this.showError({
                            errorCode: "",
                            message: errorMessage,
                            translationKey,
                        });
                    }
                    break;
                case "resetPassword":
                    router.navigate(routeFns.forgotPassword());
                    break;
                case "newPasswordRequired":
                    this.setCurrentFormAndNode(Object.assign(Object.assign({}, createNewPasswordFields({
                        h1Node: this.h1Node,
                        i18nCR: this.i18nCR,
                        onSubmitDataForm: (newValues) => __awaiter(this, void 0, void 0, function* () {
                            this.divWithSpinner.setLoading(true);
                            const nextAuthResp = yield securityModule.setNewPasswordFromForceChange(username, newValues.password, !!newValues.acceptsMarketing);
                            this.doesAcceptMarketing = !!newValues.acceptsMarketing;
                            awsCognito.tempAcceptsMarketingMails = this.doesAcceptMarketing;
                            this.didChangePassword = true;
                            yield this.onSubmitAuthForm(nextAuthResp, username);
                            this.divWithSpinner.setLoading(false);
                        }),
                    })), { formId: "newPassword" }));
                    break;
                case "totpRequired":
                    this.setCurrentFormAndNode(Object.assign(Object.assign({}, createVerifyMfaFields(this.h1Node, (newValues) => __awaiter(this, void 0, void 0, function* () {
                        this.divWithSpinner.setLoading(true);
                        const nextAuthResp = yield awsCognito.verifyTopTCode(String(newValues.code));
                        yield this.onSubmitAuthForm(nextAuthResp, username);
                        this.divWithSpinner.setLoading(false);
                    }), this.i18nCR)), { formId: "mfa" }));
                    break;
            }
            return authResp.code;
        });
        this.showError = (e) => {
            this.dialogError.show(getTranslation(`errors:errorHasOcurred`), getTranslation(typeof e === "string" ? e : e.translationKey));
            // this.dialogError.onButtonClicked = () => {
            //   this.showScreen(true);
            // };
            console.error(e);
        };
        this.i18nCR = new I18nComponentRegisterer(i18nReactive);
        this.divWithSpinner = document.createElement("div-spinner-element");
        this.h1Node = document.createElement("h1");
        setAppTitle(getTranslation("auth:logIn"));
    }
    connectedCallback() {
        this.appendChild(this.divWithSpinner);
        this.showScreen();
        goSquared.trackPage("Log-In");
        securityModule.addEventListener("loggedIn", this.onShowScreen);
        securityModule.addEventListener("loggedOut", this.onShowScreen);
    }
    disconnectedCallback() {
        this.i18nCR.disconnect();
        this.onDisconnectedCalls.forEach((fn) => fn());
        securityModule.removeEventListener("loggedIn", this.onShowScreen);
        securityModule.removeEventListener("loggedOut", this.onShowScreen);
    }
}
TVDAuthLoginComponent.observedAttributes = [];
customElements.define("tvd-auth-login-component", TVDAuthLoginComponent);
function createLoginPart({ multiStepLogin, i18nCR, initialCreateAccount, }) {
    const { newAccountForm, node: newPartNode } = createNewHerePartNodes();
    const divCenteredInteractive = document.createElement("div");
    divCenteredInteractive.className = "login-screen-interactive";
    const divCentralFormsPart = document.createElement("div");
    divCentralFormsPart.className = "login-screen-split";
    divCenteredInteractive.appendChild(divCentralFormsPart);
    divCentralFormsPart.appendChild(multiStepLogin.holder);
    divCentralFormsPart.appendChild(createSlider());
    divCentralFormsPart.appendChild(newPartNode);
    if (initialCreateAccount) {
        divCenteredInteractive.dataset.show = "newAccount";
    }
    else {
        multiStepLogin.focusOnEmail();
    }
    return {
        node: divCenteredInteractive,
    };
    function togglePart() {
        const currentPart = divCenteredInteractive.dataset.show || "";
        const isLogin = currentPart === "";
        if (isLogin) {
            divCenteredInteractive.dataset.show = "newAccount";
            newAccountForm === null || newAccountForm === void 0 ? void 0 : newAccountForm.focusOnFirstElement();
        }
        else {
            divCenteredInteractive.dataset.show = "";
            multiStepLogin.focusOnEmail();
        }
    }
    function createSlider() {
        const newHerePart = document.createElement("div");
        newHerePart.className = "login-slider";
        const card1 = document.createElement("div");
        card1.className = "card-1";
        {
            const h2 = document.createElement("h2");
            i18nCR.addConsumer(h2, "general:welcomeComp.slider1.title", "innerHTML");
            const text = document.createElement("div");
            i18nCR.addConsumer(text, "general:welcomeComp.slider1.text", "innerHTML");
            const btn = document.createElement("sl-button");
            btn.className = "login-button-home";
            btn.variant = "primary";
            i18nCR.addConsumer(btn, "general:welcomeComp.slider1.button", "innerHTML");
            btn.onclick = () => {
                togglePart();
            };
            newHerePart.appendChild(card1);
            card1.appendChild(h2);
            card1.appendChild(text);
            card1.appendChild(btn);
        }
        const card2 = document.createElement("div");
        card2.className = "card-2";
        {
            const h2 = document.createElement("h2");
            i18nCR.addConsumer(h2, "general:welcomeComp.slider2.title", "innerHTML");
            const text = document.createElement("div");
            i18nCR.addConsumer(text, "general:welcomeComp.slider2.text", "innerHTML");
            const btn = document.createElement("sl-button");
            btn.className = "login-button-home";
            btn.variant = "primary";
            i18nCR.addConsumer(btn, "general:welcomeComp.slider2.button", "innerHTML");
            btn.onclick = () => {
                togglePart();
            };
            newHerePart.appendChild(card2);
            card2.appendChild(h2);
            card2.appendChild(text);
            card2.appendChild(btn);
        }
        return newHerePart;
    }
    function createNewHerePartNodes() {
        const newHerePart = document.createElement("div");
        newHerePart.className = "login-new-here-part ";
        const ossCard = document.createElement("div");
        ossCard.className = "centered-content";
        const h2 = document.createElement("h2");
        i18nCR.addConsumer(h2, "general:welcomeComp.createNewAccount.title", "innerHTML");
        const newHere = document.createElement("div");
        newHere.className = "new-here-text";
        i18nCR.addConsumer(newHere, "general:welcomeComp.createNewAccount.text", "innerHTML");
        const newAccountBtn = document.createElement("sl-button");
        newAccountBtn.variant = "primary";
        i18nCR.addConsumer(newAccountBtn, "general:welcomeComp.createNewAccount.button", "innerHTML");
        const formValidator = z.object({
            email: z.string().email(),
        });
        const fields = [
            {
                type: "textBox",
                name: "email",
                label: "",
                placeholder: "general:common.workEmail",
            },
        ];
        const newAccountTedivoForm = new TedivoForm({
            fields: fields,
            onSubmit: onSubmitDataForm,
            formValidator: formValidator,
            submitButton: newAccountBtn,
        });
        newAccountBtn.addEventListener("click", () => {
            newAccountTedivoForm.doSubmitForm();
        });
        translateTedivoForm({
            tedivoForm: newAccountTedivoForm,
            i18nCR: i18nCR,
        });
        newHerePart.appendChild(ossCard);
        ossCard.appendChild(h2);
        ossCard.appendChild(newHere);
        ossCard.appendChild(newAccountTedivoForm.form);
        ossCard.appendChild(newAccountBtn);
        return { newAccountForm: newAccountTedivoForm, node: newHerePart };
        function onSubmitDataForm(vals) {
            router.navigate(`${routeFns.newAccount()}?email=${vals.email}`, undefined, true);
        }
    }
}
function createNewPasswordFields({ h1Node, onSubmitDataForm, i18nCR, }) {
    const AuthLoginFormValidator = z
        .object({
        password: z
            .string()
            .refine((pswd) => passwordCompliesWithCriteria(pswd).length === 0),
        retypePassword: z.string(),
        acceptsMarketing: z.boolean().optional(),
        acceptsConditions: z.boolean().refine((v) => !!v),
        acceptsPrivacyPolicy: z.boolean().refine((v) => !!v),
    })
        .refine((vals) => vals.password === vals.retypePassword, {
        path: ["retypePassword"],
    });
    const passReqs = document.createElement("div");
    passReqs.className = "oss-password-compliance-container";
    const passwordReqsTitle = document.createElement("strong");
    passwordReqsTitle.innerHTML = getTranslation("auth:passwordSchemaTitle");
    passReqs.appendChild(passwordReqsTitle);
    const ul = document.createElement("ul");
    ul.className = "oss-password-compliance";
    passReqs.appendChild(ul);
    const fields = [
        [
            {
                type: "textBox",
                name: "password",
                autoCompleteAttribute: "new-password",
                label: "auth:password",
                isPassword: true,
                helpText: "auth:passwordSchema",
                inputListener: true,
            },
            {
                type: "textBox",
                name: "retypePassword",
                label: "auth:retypePassword",
                isPassword: true,
                helpText: "auth:passwordsMustMatch",
            },
        ],
        {
            name: "password",
            type: "node",
            node: passReqs,
        },
        {
            type: "checkbox",
            name: "acceptsConditions",
            label: "general:common.acceptsConditions",
            initialValue: false,
        },
        {
            type: "checkbox",
            name: "acceptsPrivacyPolicy",
            label: "general:common.acceptsPrivacyPolicy",
            initialValue: false,
        },
        {
            type: "checkbox",
            name: "acceptsMarketing",
            label: "general:common.acceptsMarketing",
            initialValue: false,
        },
    ];
    const screen = createScreen({
        fields,
        onSubmitDataForm,
        formValidator: AuthLoginFormValidator,
        i18nCR,
        h1Text: "auth:setNewPassword",
        titleText: "auth:logIn",
        h1Node,
    });
    const tedivoForm = screen.form;
    if (tedivoForm) {
        showDetailedCriteriaCompliance(true, new Set(), ul);
        tedivoForm.onDataChange = (data, name) => {
            if (name === "password") {
                const criteriaNotMet = new Set(passwordCompliesWithCriteria(data.password));
                showDetailedCriteriaCompliance(false, criteriaNotMet, ul);
            }
        };
    }
    return screen;
}
function createVerifyMfaFields(h1Node, onSubmitDataForm, i18nCR) {
    const AuthMfaFormValidator = z
        .object({
        code: z.string(),
    })
        .refine((vals) => vals.code.length === 6);
    const fields = [
        {
            type: "textBox",
            name: "code",
            label: "auth:mfa.mfaCode",
            autoCompleteAttribute: "one-time-code",
            isPassword: false,
        },
    ];
    const screen = createScreen({
        fields,
        onSubmitDataForm,
        formValidator: AuthMfaFormValidator,
        i18nCR,
        h1Text: "auth:mfa.mfaCodePlaceholder",
        titleText: "auth:logIn",
        h1Node,
    });
    return screen;
}
function createLogoH1Replacement(h1Node) {
    const icon1 = document.createElement("img");
    icon1.setAttribute("title", getTranslation("general:appName"));
    icon1.setAttribute("alt", getTranslation("general:appName"));
    icon1.setAttribute("width", "522");
    icon1.setAttribute("heigth", "136");
    icon1.setAttribute("src", "/assets/images/tedivo-designer-horizontal-tagline-color.svg");
    icon1.className = "tvd-h1-icon logo-light";
    const icon2 = document.createElement("img");
    icon2.setAttribute("title", getTranslation("general:appName"));
    icon2.setAttribute("alt", getTranslation("general:appName"));
    icon2.setAttribute("width", "522");
    icon2.setAttribute("heigth", "136");
    icon2.setAttribute("src", "/assets/images/tedivo-designer-horizontal-tagline-color-knockout.svg");
    icon2.className = "tvd-h1-icon logo-dark";
    removeChildren(h1Node);
    h1Node.appendChild(icon1);
    h1Node.appendChild(icon2);
}
